/**
 * Optional device geolocation for the session screens.
 *
 * Every screen in this feature that can use a live position (the start-point
 * picker, the guidance screen) must keep working without one: refusal,
 * absence of the API, or simply never asking are all normal outcomes, never
 * errors. `navigator.geolocation` is only ever invoked from
 * {@link OptionalPosition.requestPosition}, itself only ever called from an
 * explicit tap ("Utiliser ma position") — except once automatically on
 * mount when a *previous* explicit tap, earlier in this browser tab, already
 * granted it (`sessionStorage`); the browser does not re-prompt in that
 * case, it silently hands back a fresh fix, so a later screen (guidance)
 * keeps benefiting from an earlier "yes" without asking again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { GeoPoint } from '../../../api/sessions';

const STORAGE_KEY = 'dring:geolocation-consent';

/** Read whether geolocation was granted earlier in this browser tab. */
function readStoredConsent(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'granted';
  } catch {
    // Private browsing, or storage disabled entirely: never block on it.
    return false;
  }
}

/** Remember the volunteer's answer for the rest of this browser tab. */
function writeStoredConsent(value: 'granted' | 'denied'): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Same as above: a lost preference is a safe degradation, not a blocker.
  }
}

export interface OptionalPosition {
  /** The last known position, or `null` while unknown, refused, or unsupported. */
  position: GeoPoint | null;
  /** Whether geolocation was granted, in this call or an earlier one this tab. */
  granted: boolean;
  /** Ask for the position once — the only intentional call site of `navigator.geolocation`. */
  requestPosition: () => void;
}

/**
 * Track an optional device position without ever blocking the interface.
 *
 * @returns The current position, whether it was granted, and the requester.
 */
export function useOptionalPosition(): OptionalPosition {
  const [granted, setGranted] = useState<boolean>(readStoredConsent);
  const [position, setPosition] = useState<GeoPoint | null>(null);
  const watchIdRef = useRef<number | undefined>(undefined);

  const startWatching = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (fix) => {
        setPosition({ longitude: fix.coords.longitude, latitude: fix.coords.latitude });
      },
      () => {
        // A fix briefly failing (tunnel, lift) must not clear an earlier one.
      },
      { enableHighAccuracy: true, maximumAge: 30_000 },
    );
  }, []);

  const requestPosition = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        setPosition({ longitude: fix.coords.longitude, latitude: fix.coords.latitude });
        setGranted(true);
        writeStoredConsent('granted');
        startWatching();
      },
      () => {
        // Refusal, timeout, or no fix available: stay silent — the caller
        // keeps working from the session's `start_point` instead.
        writeStoredConsent('denied');
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }, [startWatching]);

  useEffect(() => {
    if (granted) {
      requestPosition();
    }
    return () => {
      if (watchIdRef.current !== undefined && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
    // Intentionally runs once on mount only: re-running on every render
    // would restart the watch and re-request a fix in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { position, granted, requestPosition };
}
